import { useCallback } from 'react';

import {
  useAllZoomLessonsQuery,
  AllZoomLessonsQueryVariables,
  ZoomLessonFieldsFragment,
} from 'core/graphql';

export type ZoomLessonRow = ZoomLessonFieldsFragment;

type ZoomLessonsFilters = Omit<AllZoomLessonsQueryVariables, 'first' | 'after'>;

interface IZoomLessonsHook {
  lessons: ZoomLessonRow[];
  loading: boolean;
  error?: string;
  hasNextPage: boolean;
  startPolling: (interval: number) => void;
  stopPolling: () => void;
  operations: {
    fetchMore: () => void;
    refetch: () => void;
  };
}

/**
 * Lesson-centric list: every GroupLesson in a date range, with its optional
 * `zoomRecordingMeta`. Relay-cursor paginated — `fetchMore` appends the next
 * page; changing `filters` re-runs the query from scratch.
 */
export const useZoomLessons = (
  filters: ZoomLessonsFilters,
  pageSize = 50,
): IZoomLessonsHook => {
  const {
    data,
    loading,
    error,
    fetchMore,
    refetch,
    startPolling,
    stopPolling,
  } = useAllZoomLessonsQuery({
    variables: { ...filters, first: pageSize },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const connection = data?.allZoomLessons;
  const edges = connection?.edges ?? [];

  const handleFetchMore = useCallback(() => {
    if (!connection?.pageInfo?.hasNextPage) {
      return;
    }

    fetchMore({
      variables: {
        ...filters,
        first: pageSize,
        after: connection.pageInfo.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.allZoomLessons) {
          return prev;
        }

        return {
          allZoomLessons: {
            ...fetchMoreResult.allZoomLessons,
            edges: [
              ...(prev.allZoomLessons?.edges ?? []),
              ...fetchMoreResult.allZoomLessons.edges,
            ],
          },
        };
      },
    });
  }, [connection, fetchMore, filters, pageSize]);

  return {
    lessons: edges
      .map((edge) => edge?.node)
      .filter((node): node is ZoomLessonRow => Boolean(node)),
    loading,
    error: error?.message,
    hasNextPage: Boolean(connection?.pageInfo?.hasNextPage),
    startPolling,
    stopPolling,
    operations: {
      fetchMore: handleFetchMore,
      refetch: () => {
        refetch();
      },
    },
  };
};
